import { cssRule } from "typestyle";
import { ContentManagement, Share, Sizes, TsConfig } from "../API/TsConfig";
import { getContentUrl } from "../StaticAssets";
import { isAuthoring, isPublic } from "../StaticConfig";
import {
  DeviceLayoutPreference,
  getSizeConstraints,
} from "./GetSizeConstraints";

export function handleTsConfigForTableauPublic(
  doc: Document,
  tsConfig: TsConfig
) {
  showStaticImage(doc, tsConfig);
  addLyteboxCloseButtonToDOM(doc, tsConfig);
  linkToPrimaryContent(doc, tsConfig);
  addUnfurlTagsToHead(doc, tsConfig);
}

export function updateInitialPublicPage(doc: Document) {
  if (isPublic(doc)) {
    doc.body.classList.add("public");
    const spinnerContainer = doc.getElementById("svg-spinner-container");
    if (spinnerContainer) {
      spinnerContainer.style.background = "rgba(255,255,255,.6)";
    }
  }
}

function showStaticImage(doc: Document, tsConfig: TsConfig) {
  if (!tsConfig.bootstrapOnMouseover) {
    return;
  }

  const staticImage = doc.getElementById("staticImage");
  if (staticImage !== null) {
    const topPadding = tsConfig.showTabs ? 31 : 9;
    const staticImageUrl = tsConfig.staticImage.replace(/_rss/g, "");
    staticImage.style.backgroundColor = "transparent";
    staticImage.style.background = `no-repeat url(${staticImageUrl})`;
    staticImage.style.left = "4px";
    staticImage.style.top = `${topPadding}px`;
    staticImage.style.width = "auto";
    staticImage.style.height = "auto";
    staticImage.style.display = "block";
  }
}

export function linkToPrimaryContent(
  doc: Document,
  tsConfig: ContentManagement
) {
  if (!tsConfig.primaryContentUrl) {
    return;
  }
  const anchor = doc.getElementById("primaryContentLink") as HTMLAnchorElement;
  anchor.href = tsConfig.primaryContentUrl;
}

export type Dimensions = {
  width: number;
  height: number;
};

function getLyteboxDimensions(
  sizeToUse: Sizes,
  tsConfig: ContentManagement
): Dimensions {
  // The lytebox needs to be at least as big as the viz it contains. Otherwise
  // the content will overflow the lytebox container and we will have messed up centering
  let width = 0;
  let height = 0;
  if (sizeToUse != null) {
    width = Math.max(sizeToUse.maxWidth, sizeToUse.minWidth);
    height = Math.max(sizeToUse.maxHeight, sizeToUse.minHeight);
  }

  // if view has no size constraints, use a fixed size when lyteboxing
  if (width === 0) {
    width = tsConfig.public_lytebox_default_width || 600;
  }
  if (height === 0) {
    height = tsConfig.public_lytebox_default_height || 800;
  }

  return { width, height };
}

export function addLyteboxCloseButtonToDOM(
  doc: Document,
  tsConfig: ContentManagement
) {
  if (!tsConfig.primaryContentUrl) {
    return;
  }

  const precedingElement = doc.getElementById("tabBootErr");
  if (!precedingElement) {
    return;
  }

  const sizeToUse = getSizeConstraints(
    tsConfig,
    isAuthoring(doc) || false,
    DeviceLayoutPreference.Desktop
  );
  const lyteboxDimensions = getLyteboxDimensions(sizeToUse, tsConfig);
  addLyteboxStyles(tsConfig, lyteboxDimensions.width, lyteboxDimensions.height);

  const closeButton = doc.createElement("img");
  closeButton.height = 27;
  closeButton.width = 28;
  closeButton.src = getContentUrl("images/close_button.png");

  const primaryContentAnchor = doc.createElement("a");
  primaryContentAnchor.setAttribute("href", tsConfig.primaryContentUrl);
  primaryContentAnchor.appendChild(closeButton);

  const lyteboxClose = doc.createElement("div");
  lyteboxClose.setAttribute("id", "lyteboxClose");
  lyteboxClose.appendChild(primaryContentAnchor);

  precedingElement.after(lyteboxClose);
}

function addLyteboxStyles(
  tsConfig: ContentManagement,
  lyteboxWidth: number,
  lyteboxHeight: number
) {
  cssRule("body.lyteboxed", {
    minWidth: `${lyteboxWidth + 60}px;`,
  });

  cssRule("#centeringContainer.lyteboxed", {
    width: `${lyteboxWidth}px`,
    marginLeft: `${-(lyteboxWidth / 2)}px`,
    minHeight: `${lyteboxHeight}px`,
    position: "relative",
    top: 0,
    left: "50%",
    marginTop: "30px",
    marginBottom: "30px",
    boxShadow: "2px 2px 40px rgba(0, 0, 0, 0.65)",
    backgroundColor: "white",
  });

  cssRule("#lyteboxClose", {
    position: "absolute",
    top: "-14px",
    [tsConfig.showTabs ? "left" : "right"]: "-14px",
    display: "none",
    zIndex: 100,
  });

  cssRule("#lyteboxClose a img", {
    border: "none",
  });

  cssRule("#primaryContent, #primaryContentLink", {
    position: "fixed",
    height: "100%",
    width: "100%",
    top: 0,
    border: 0,
    display: "block",
  });

  cssRule("#primaryContentLink", {
    backgroundColor: "rgba(0,0,0,0.4)",
    left: 0,
  });

  cssRule(".lyteboxed .wcBody .dashboardSpacer .tab-dashboard", {
    overflow: "hidden",
  });
}

function createTwitterCreatorTag() {
  const twitterCreator = document.createElement("head");
  twitterCreator.setAttribute("name", "twitter:creator");
  twitterCreator.setAttribute("id", "twitter_creator");
  twitterCreator.setAttribute("content", ""); // likely bug - should be a handle
  return twitterCreator;
}

type UnfurlTag = ["property" | "name", string, string];

export function addUnfurlTagsToHead(
  doc: Document,
  tsConfig: Share & ContentManagement
) {
  const tags = new Array<UnfurlTag>(
    ["property", "fb:app_id", tsConfig.facebookAppID],
    ["property", "og:title", tsConfig.shareDescription],
    ["property", "og:type", "article"],
    ["property", "og:image", tsConfig.baseViewThumbLink],
    ["property", "og:site_name", "Tableau Software"],
    ["name", "twitter:card", "summary"],
    ["name", "twitter:site", "@tableau"],
    ["name", "twitter:title", tsConfig.workbookName],
    ["name", "twitter:description", tsConfig.shareDescription],
    ["name", "twitter:image:src", tsConfig.baseViewThumbLink],
    ["name", "twitter:domain", "Tableau Public"]
  );
  tags.forEach((tagItem: UnfurlTag) => {
    const tag = doc.createElement("meta");
    const [attrName, attrValue, contentValue] = tagItem;
    tag.setAttribute(attrName, attrValue);
    tag.setAttribute("content", contentValue);
    doc.head.append(tag);
  });
  doc.head.append(createTwitterCreatorTag());
}
